import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import { LayoutDashboard, BookOpen, ClipboardCheck, Video, ShieldCheck, Users, Library, Film, WalletCards, BriefcaseBusiness, Gift } from "lucide-react";

export default function MobileNav({ role }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarSettings, setSidebarSettings] = useState({ student: {}, teacher: {} });

  useEffect(() => {
    if (role === "student" || role === "teacher") {
      api.get("/settings/sidebar_visibility").then(res => {
        if (res.data) setSidebarSettings(res.data);
      }).catch(err => console.error("Failed to fetch sidebar settings"));
    }
  }, [role]);

  const mapLabelToFeature = (label) => {
    if (label === "Home") return "Dashboard";
    if (label === "Exams") return role === "student" ? "Online Exams" : "Manage Exams";
    if (label === "Books") return "Recommended Books";
    if (label === "Plans") return "Subscription Plans";
    if (label === "Placement") return "Placement Preparation";
    if (label === "Refer") return "Referral & Affiliate";
    if (label === "Live") return "Live Classes";
    if (label === "Recordings") return "Recorded Video";
    if (label === "New For You" && role === "teacher") return "My Courses";
    return label;
  };

  const isVisible = (label) => {
    if (role !== "student" && role !== "teacher") return true;
    const feature = mapLabelToFeature(label);
    return sidebarSettings[role]?.[feature] !== false;
  };

  const menuItems = [
    {
      label: "Home",
      icon: LayoutDashboard,
      path: role === "teacher" ? "/teacher-dashboard" : role === "parent" ? "/parent-dashboard" : "/dashboard"
    },
    ...(role !== "parent" ? [{
      label: "New For You",
      icon: BookOpen,
      path: role === "teacher" ? "/teacher-dashboard" : "/dashboard"
    }] : []),
    ...(role === "teacher" ? [{ label: "Exams", icon: ClipboardCheck, path: "/teacher-exams" }] : []),
    ...(role === "student" ? [
      { label: "Exams", icon: ClipboardCheck, path: "/exams" },
      { label: "Plans", icon: WalletCards, path: "/subscription-plans" },
      { label: "Placement", icon: BriefcaseBusiness, path: "/placement-preparation" },
      { label: "Refer", icon: Gift, path: "/referrals" },
      { label: "Books", icon: Library, path: "/library" }
    ] : []),
    ...(role === "parent" ? [{ label: "Children", icon: Users, path: "/parent-dashboard" }] : []),
    ...(role !== "parent" ? [
      { label: "Live", icon: Video, path: "/live-classes" },
      { label: "Recordings", icon: Film, path: "/recordings" }
    ] : []),
    ...(role === "admin" ? [{ label: "Admin", icon: ShieldCheck, path: "/admin" }] : []),
  ];

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 flex items-center gap-1 overflow-x-auto border-t border-slate-200 bg-white/95 px-3 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] shadow-[0_-12px_30px_rgba(15,23,42,0.08)] backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/95 no-scrollbar">
      {menuItems.filter(item => isVisible(item.label)).map((item) => {
        const isActive = location.pathname === item.path;
        return (
          <button
            key={item.label}
            onClick={() => navigate(item.path)}
            className={`flex min-w-[4.5rem] flex-col items-center gap-1 rounded-lg px-2 py-2 transition-all ${isActive ? "bg-indigo-50 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-300" : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-900"
              }`}
          >
            <item.icon size={21} className={isActive ? "scale-110" : ""} />
            <span className="max-w-full truncate text-[10px] font-bold">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
